import React, { useEffect, useState } from 'react';

import { Anchor, Badge, Center, Group, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { IconBarbell, IconTrophy } from '@tabler/icons-react';
import MovementTypeBadge from 'components/status-badges/MovementTypeBadge';
import Table from 'components/Table';
import useWorkoutHistory from 'hooks/workoutHistory/useWorkoutHistory';
import { CellProps } from 'react-table';
import { WorkoutHistoryRow } from 'types';
import { SCORE_TYPE_LABEL_MAP, ScoreTypeEnum, WORKOUT_LEVEL_LABEL_MAP, WorkoutLevelEnum } from 'types/enums';
import { formatDateForDisplay } from 'utils/date-functions';

// Sortable columns, mapped to the field the endpoint orders on. Anything absent renders
// as a plain header.
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  name: 'name',
  latestDate: 'date',
};

const Dash = (): JSX.Element => (
  <Text color='dimmed' size='sm'>
    &mdash;
  </Text>
);

// A level-less result carries no badge: the API sends the enum or null, never a label.
const levelLabel = (level: NonNullable<WorkoutHistoryRow['level']>): string =>
  WORKOUT_LEVEL_LABEL_MAP[level as WorkoutLevelEnum];

// Shown only when the athlete has logged nothing at all, never when filters exclude
// every row.
const NoResultsYet = (): JSX.Element => {
  const theme = useMantineTheme();
  return (
    <Center py='xl'>
      <Stack align='center' spacing='xs' maw={420}>
        <IconBarbell size={40} color={theme.colors.kiloBlue[4]} />
        <Title order={4}>No results yet</Title>
        <Text color='dimmed' align='center'>
          This athlete hasn&apos;t logged any results yet. Results will appear here once they start logging
          workouts from the Athlete App.
        </Text>
      </Stack>
    </Center>
  );
};

const WorkoutHistoryTable = ({
  athleteId,
  searchFilters,
  openDetail,
  sortColumn,
  ascending,
  handleHeaderClick,
  totalResults,
}: {
  athleteId: string;
  searchFilters: {
    search: string;
    type: string;
    startDateRange?: string;
    endDateRange?: string;
  };
  openDetail: (row: WorkoutHistoryRow) => void;
  sortColumn: string;
  ascending: boolean;
  handleHeaderClick: (column: string) => void;
  // The athlete's unfiltered row count, undefined until it is known. A known zero is
  // the only case that means "has never logged anything".
  totalResults?: number;
}): JSX.Element => {
  const theme = useMantineTheme();
  const [tableData, setTableData] = useState<WorkoutHistoryRow[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useWorkoutHistory({
      athleteId,
      search: searchFilters.search,
      type: searchFilters.type,
      startDateRange: searchFilters.startDateRange,
      endDateRange: searchFilters.endDateRange,
      ordering: `${ascending ? '' : '-'}${SORT_FIELD_BY_COLUMN[sortColumn]}`,
    });

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'name' as const,
      // The name opens the detail, the way the athletes list opens an athlete. A
      // hard-deleted movement has no id to read a history by, so its name is plain text.
      Cell: ({ row }: CellProps<WorkoutHistoryRow>) =>
        row.original.workoutMovementId ? (
          <Anchor
            component='button'
            type='button'
            size='sm'
            weight='bold'
            // Anchor's root forces text-decoration: none, so the underline goes in sx,
            // where it outranks it. Colour inherits so this reads like the athletes list.
            sx={{ color: 'inherit', textDecoration: 'underline' }}
            onClick={() => openDetail(row.original)}
          >
            {row.original.name}
          </Anchor>
        ) : (
          <Text size='sm'>{row.original.name}</Text>
        ),
    },
    {
      Header: 'Type',
      accessor: 'movementType' as const,
      Cell: ({ row }: CellProps<WorkoutHistoryRow>) => (
        <MovementTypeBadge movementType={row.original.movementType} size='sm' />
      ),
    },
    {
      Header: 'Score Type',
      accessor: 'scoreType' as const,
      Cell: ({ row }: CellProps<WorkoutHistoryRow>) => (
        <Text size='sm' color='dimmed'>
          {SCORE_TYPE_LABEL_MAP[row.original.scoreType as ScoreTypeEnum] ?? row.original.scoreType}
        </Text>
      ),
    },
    {
      Header: 'Last Result',
      accessor: 'latestResult' as const,
      Cell: ({ row }: CellProps<WorkoutHistoryRow>) => (
        <Group spacing='xs' noWrap>
          <Text size='sm'>{row.original.latestResult}</Text>
          {row.original.isPr && (
            <IconTrophy
              size={theme.fontSizes.md}
              color={theme.colors.yellow[6]}
              aria-label='Personal record'
            />
          )}
          {row.original.level && (
            <Badge variant='outline' size='xs' color='gray'>
              {levelLabel(row.original.level)}
            </Badge>
          )}
        </Group>
      ),
    },
    {
      Header: 'Best Result',
      accessor: 'bestResult' as const,
      Cell: ({ row }: CellProps<WorkoutHistoryRow>) =>
        row.original.bestResult ? (
          <Group spacing='xs' noWrap>
            <Text size='sm'>{row.original.bestResult}</Text>
            {row.original.bestResultLevel && (
              <Badge variant='outline' size='xs' color='gray'>
                {levelLabel(row.original.bestResultLevel)}
              </Badge>
            )}
          </Group>
        ) : (
          <Dash />
        ),
    },
    {
      Header: 'Last Date',
      accessor: 'latestDate' as const,
      Cell: ({ row }: CellProps<WorkoutHistoryRow>) =>
        row.original.latestDate ? (
          <Text size='sm'>{formatDateForDisplay(row.original.latestDate)}</Text>
        ) : (
          <Dash />
        ),
    },
  ];

  return (
    <Table
      tableData={tableData}
      columns={columns}
      isSuccess={isSuccess}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      clickableHeaders={Object.keys(SORT_FIELD_BY_COLUMN)}
      handleHeaderClick={handleHeaderClick}
      ascending={ascending}
      ordering={sortColumn}
      // Only a known-zero unfiltered count gets the never-logged copy. While the count
      // is still unknown, and whenever rows exist, Table's own "no results found"
      // covers the filtered-to-nothing case.
      EmptyState={totalResults === 0 ? <NoResultsYet /> : undefined}
    />
  );
};

export default WorkoutHistoryTable;
